import { useMemo, useState } from 'react'
import { Box, Button, Paper, Stack, TextField, Typography } from '@mui/material'
import { useOnboarding } from '../../context/OnboardingContext'
import { isOnboardingContactComplete, type OnboardingContact } from '../../types/onboarding'
import { MISC_PRIMARY, MISC_PRIMARY_HOVER } from '../../theme/onboardingTheme'

const ROLE_SUGGESTIONS = [
  'SL Admin',
  'Accounting',
  'Surplus Lines Manager',
  'Compliance',
  'Claims',
  'Operations',
]

const emptyAddForm = {
  role: '',
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
}

function contactFieldSx(editing: boolean, opts?: { emailIdle?: boolean }) {
  return {
    mt: 1,
    width: '100%',
    '& .MuiOutlinedInput-root': {
      fontSize: 14,
      backgroundColor: editing ? '#ffffff' : 'rgba(248, 250, 252, 0.4)',
      color: '#0f172a',
      cursor: editing ? 'text' : 'default',
      '& fieldset': {
        borderColor: editing ? '#cbd5e1' : 'transparent',
      },
      '&:hover fieldset': {
        borderColor: editing ? '#cbd5e1' : 'transparent',
      },
      '&.Mui-focused fieldset': {
        borderColor: editing ? MISC_PRIMARY : 'transparent',
        borderWidth: editing ? 2 : 1,
      },
    },
    '& .MuiOutlinedInput-input': {
      cursor: editing ? 'text' : 'default',
      py: 0.75,
      px: 1,
      color: !editing && opts?.emailIdle ? MISC_PRIMARY : '#0f172a',
    },
  }
}

const addFieldSx = {
  mt: 1,
  width: '100%',
  '& .MuiOutlinedInput-root': {
    fontSize: 14,
    backgroundColor: '#ffffff',
    '& fieldset': { borderColor: '#cbd5e1' },
  },
  '& .MuiOutlinedInput-input': {
    py: 0.75,
    px: 1,
  },
}

export function StepContacts() {
  const { state, dispatch } = useOnboarding()
  const [showAddForm, setShowAddForm] = useState(false)
  const [addForm, setAddForm] = useState(emptyAddForm)

  const allComplete = useMemo(
    () => state.contacts.length > 0 && state.contacts.every((c) => isOnboardingContactComplete(c)),
    [state.contacts],
  )

  const confirmDisabled = state.contactsConfirmed || !allComplete

  const submitAddContact = () => {
    const role = addForm.role.trim()
    const firstName = addForm.firstName.trim()
    const lastName = addForm.lastName.trim()
    const phone = addForm.phone.trim()
    const email = addForm.email.trim()
    if (!role || !firstName || !lastName || !phone || !email) {
      dispatch({
        type: 'TOAST',
        message: 'Fill out role, first name, last name, phone, and email before adding.',
      })
      return
    }
    const contact: OnboardingContact = {
      id: `c-${Date.now()}`,
      role,
      firstName,
      lastName,
      phone,
      email,
    }
    dispatch({ type: 'ADD_CONTACT', contact })
    setAddForm(emptyAddForm)
    setShowAddForm(false)
    dispatch({ type: 'TOAST', message: 'Contact added (demo).' })
  }

  return (
    <Stack sx={{ minWidth: 0 }} spacing={4}>
      <Typography sx={{ fontSize: 14, color: '#475569', maxWidth: { lg: 'none' } }}>
        Please review the contacts below and confirm that they are correct.
      </Typography>

      <datalist id="contact-role-suggestions">
        {ROLE_SUGGESTIONS.map((r) => (
          <option key={r} value={r} />
        ))}
      </datalist>

      <Box
        sx={{
          display: 'grid',
          gap: 2.5,
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', xl: '1fr 1fr 1fr' },
        }}
      >
        {state.contacts.map((c) => (
          <Paper
            key={c.id}
            variant="outlined"
            sx={{
              minWidth: 0,
              borderColor: '#e2e8f0',
              borderRadius: '12px',
              p: 2.5,
              boxShadow: 1,
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gap: 1.5,
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' },
              }}
            >
              <Box
                component="label"
                sx={{ display: 'block', gridColumn: { sm: '1 / -1', lg: '1 / -1' } }}
              >
                <Typography
                  component="span"
                  sx={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', color: '#64748b' }}
                >
                  Role
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  InputProps={{ readOnly: !state.contactsEditing }}
                  inputProps={{
                    list: state.contactsEditing ? 'contact-role-suggestions' : undefined,
                  }}
                  value={c.role}
                  onChange={(e) =>
                    dispatch({ type: 'UPDATE_CONTACT', id: c.id, patch: { role: e.target.value } })
                  }
                  sx={contactFieldSx(state.contactsEditing)}
                  placeholder="e.g. SL Admin, Accounting"
                />
              </Box>
              <Box component="label" sx={{ display: 'block' }}>
                <Typography
                  component="span"
                  sx={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', color: '#64748b' }}
                >
                  First name
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  InputProps={{ readOnly: !state.contactsEditing }}
                  value={c.firstName}
                  onChange={(e) =>
                    dispatch({
                      type: 'UPDATE_CONTACT',
                      id: c.id,
                      patch: { firstName: e.target.value },
                    })
                  }
                  sx={contactFieldSx(state.contactsEditing)}
                />
              </Box>
              <Box component="label" sx={{ display: 'block' }}>
                <Typography
                  component="span"
                  sx={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', color: '#64748b' }}
                >
                  Last name
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  InputProps={{ readOnly: !state.contactsEditing }}
                  value={c.lastName}
                  onChange={(e) =>
                    dispatch({
                      type: 'UPDATE_CONTACT',
                      id: c.id,
                      patch: { lastName: e.target.value },
                    })
                  }
                  sx={contactFieldSx(state.contactsEditing)}
                />
              </Box>
              <Box
                component="label"
                sx={{ display: 'block', gridColumn: { sm: 'span 2', lg: 'span 2' } }}
              >
                <Typography
                  component="span"
                  sx={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', color: '#64748b' }}
                >
                  Phone
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  InputProps={{ readOnly: !state.contactsEditing }}
                  value={c.phone}
                  onChange={(e) =>
                    dispatch({ type: 'UPDATE_CONTACT', id: c.id, patch: { phone: e.target.value } })
                  }
                  sx={contactFieldSx(state.contactsEditing)}
                />
              </Box>
              <Box
                component="label"
                sx={{ display: 'block', gridColumn: { sm: 'span 2', lg: 'span 2' } }}
              >
                <Typography
                  component="span"
                  sx={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', color: '#64748b' }}
                >
                  Email
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  type="email"
                  InputProps={{ readOnly: !state.contactsEditing }}
                  value={c.email}
                  onChange={(e) =>
                    dispatch({ type: 'UPDATE_CONTACT', id: c.id, patch: { email: e.target.value } })
                  }
                  sx={contactFieldSx(state.contactsEditing, { emailIdle: true })}
                />
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>

      {showAddForm ? (
        <Paper
          variant="outlined"
          sx={{
            borderStyle: 'dashed',
            borderColor: 'rgba(37, 99, 235, 0.4)',
            backgroundColor: 'rgba(239, 246, 255, 0.4)',
            borderRadius: '12px',
            p: 2.5,
          }}
        >
          <Typography sx={{ mb: 1.5, fontSize: 12, fontWeight: 500, color: '#334155' }}>
            Add contact (all fields required)
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' },
            }}
          >
            <Box
              component="label"
              sx={{ display: 'block', fontSize: 12, gridColumn: { sm: '1 / -1', lg: '1 / -1' } }}
            >
              <Typography component="span" sx={{ fontWeight: 500, color: '#475569' }}>
                Role
              </Typography>
              <TextField
                size="small"
                fullWidth
                inputProps={{ list: 'contact-role-suggestions' }}
                placeholder="SL Admin, Accounting, etc."
                value={addForm.role}
                onChange={(e) => setAddForm((f) => ({ ...f, role: e.target.value }))}
                sx={addFieldSx}
              />
            </Box>
            <Box component="label" sx={{ display: 'block', fontSize: 12 }}>
              <Typography component="span" sx={{ fontWeight: 500, color: '#475569' }}>
                First name
              </Typography>
              <TextField
                size="small"
                fullWidth
                value={addForm.firstName}
                onChange={(e) => setAddForm((f) => ({ ...f, firstName: e.target.value }))}
                sx={addFieldSx}
              />
            </Box>
            <Box component="label" sx={{ display: 'block', fontSize: 12 }}>
              <Typography component="span" sx={{ fontWeight: 500, color: '#475569' }}>
                Last name
              </Typography>
              <TextField
                size="small"
                fullWidth
                value={addForm.lastName}
                onChange={(e) => setAddForm((f) => ({ ...f, lastName: e.target.value }))}
                sx={addFieldSx}
              />
            </Box>
            <Box
              component="label"
              sx={{ display: 'block', fontSize: 12, gridColumn: { sm: 'span 2', lg: 'span 2' } }}
            >
              <Typography component="span" sx={{ fontWeight: 500, color: '#475569' }}>
                Phone
              </Typography>
              <TextField
                size="small"
                fullWidth
                value={addForm.phone}
                onChange={(e) => setAddForm((f) => ({ ...f, phone: e.target.value }))}
                sx={addFieldSx}
              />
            </Box>
            <Box
              component="label"
              sx={{ display: 'block', fontSize: 12, gridColumn: { sm: 'span 2', lg: 'span 2' } }}
            >
              <Typography component="span" sx={{ fontWeight: 500, color: '#475569' }}>
                Email
              </Typography>
              <TextField
                size="small"
                fullWidth
                type="email"
                value={addForm.email}
                onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))}
                sx={addFieldSx}
              />
            </Box>
          </Box>
          <Stack direction="row" flexWrap="wrap" spacing={1} sx={{ mt: 2 }}>
            <Button
              type="button"
              variant="contained"
              onClick={submitAddContact}
              sx={{
                backgroundColor: MISC_PRIMARY,
                px: 2,
                py: 1,
                fontSize: 12,
                fontWeight: 600,
                color: '#ffffff',
                '&:hover': { backgroundColor: MISC_PRIMARY_HOVER },
              }}
            >
              Save contact
            </Button>
            <Button
              type="button"
              variant="outlined"
              onClick={() => {
                setShowAddForm(false)
                setAddForm(emptyAddForm)
              }}
              sx={{
                borderColor: '#cbd5e1',
                px: 2,
                py: 1,
                fontSize: 12,
                fontWeight: 600,
                color: '#334155',
                '&:hover': { backgroundColor: '#f8fafc' },
              }}
            >
              Cancel
            </Button>
          </Stack>
        </Paper>
      ) : null}

      <Stack
        direction="row"
        flexWrap="wrap"
        alignItems="center"
        spacing={1.5}
        sx={{ borderTop: '1px solid #f1f5f9', pt: 1 }}
      >
        <Button
          type="button"
          variant="contained"
          disabled={confirmDisabled}
          onClick={() => dispatch({ type: 'CONFIRM_CONTACTS' })}
          sx={{
            backgroundColor: MISC_PRIMARY,
            px: 2.5,
            py: 1.25,
            fontSize: 14,
            fontWeight: 600,
            color: '#ffffff',
            '&:hover': { backgroundColor: MISC_PRIMARY_HOVER },
            '&.Mui-disabled': { opacity: 0.5, color: '#ffffff' },
          }}
        >
          Confirm
        </Button>
        <Button
          type="button"
          variant="outlined"
          onClick={() =>
            dispatch({ type: 'SET_CONTACTS_EDITING', editing: !state.contactsEditing })
          }
          sx={{
            px: 2.5,
            py: 1.25,
            fontSize: 14,
            fontWeight: 600,
            ...(state.contactsEditing
              ? {
                  borderColor: MISC_PRIMARY,
                  backgroundColor: '#eff6ff',
                  color: MISC_PRIMARY,
                }
              : {
                  borderColor: '#cbd5e1',
                  color: '#334155',
                  '&:hover': { backgroundColor: '#f8fafc' },
                }),
          }}
        >
          {state.contactsEditing ? 'Done' : 'Edit'}
        </Button>
        <Button
          type="button"
          disabled={showAddForm}
          variant="outlined"
          onClick={() => setShowAddForm(true)}
          sx={{
            borderColor: '#cbd5e1',
            px: 2.5,
            py: 1.25,
            fontSize: 14,
            fontWeight: 600,
            color: '#334155',
            '&:hover': { backgroundColor: '#f8fafc' },
            '&.Mui-disabled': { opacity: 0.5 },
          }}
        >
          Add Contact
        </Button>
      </Stack>

      {!allComplete ? (
        <Typography sx={{ fontSize: 12, color: '#92400e' }}>
          Every contact must have role, first name, last name, phone, and email before you can
          confirm.
        </Typography>
      ) : null}
    </Stack>
  )
}
